import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, MapPin, MessageCircle, PhoneCall, Smartphone, Clock, ShieldCheck } from "lucide-react";
import { profile } from "@/data/portfolio";

const mailHref = `mailto:${profile.email}?subject=${encodeURIComponent(
  "Web sitesi teklifi"
)}&body=${encodeURIComponent(
  "Merhaba,\n\nİşletme adı:\nSektör:\nİlgilendiğim paket (Tek Sayfa / Standart / Kapsamlı):\n\nTeşekkürler."
)}`;

const reasons = [
  {
    icon: ShieldCheck,
    title: "Güven verir",
    text: "Müşteri sizi aradığında işletmenizin ciddi, güncel ve ulaşılabilir olduğunu ilk bakışta görür.",
  },
  {
    icon: PhoneCall,
    title: "Tek dokunuşla ulaşım",
    text: "Ara, WhatsApp'tan yaz, yol tarifi al düğmeleri telefonda hep elinizin altında olur.",
  },
  {
    icon: MapPin,
    title: "Sizin sayfanız",
    text: "Hizmetlerinizi, saatlerinizi ve konumunuzu sosyal medya ya da harita kaydına bağımlı kalmadan kendi adresinizde anlatırsınız.",
  },
];

const plans = [
  {
    name: "Tek Sayfa",
    price: "1.499 TL",
    time: "2 günde teslim",
    highlight: false,
    items: [
      "Tek sayfalık, mobil uyumlu işletme sitesi",
      "Hizmetler, çalışma saatleri, konum haritası",
      "Ara ve WhatsApp düğmeleri",
      "Alan adınıza bağlantı veya yayın kurulumu",
    ],
  },
  {
    name: "Profesyonel",
    price: "3.999 TL",
    time: "5 günde teslim",
    highlight: true,
    items: [
      "5 sayfaya kadar (Ana Sayfa, Hizmetler, Hakkımızda, Galeri, İletişim)",
      "Fotoğraf galerisi ve sık sorulan sorular",
      "Sizin sağladığınız müşteri yorumları bölümü",
      "Türkçe ve İngilizce seçeneği",
      "Randevu / teklif talebi için WhatsApp yönlendirmesi",
      "Arama motoru için başlık, açıklama ve harita işaretlemesi",
    ],
  },
  {
    name: "Premium",
    price: "14.999 TL",
    time: "15 günde teslim",
    highlight: false,
    items: [
      "Size özel tasarım, 10 sayfaya kadar",
      "İçeriği kendiniz güncelleyeceğiniz yönetim paneli",
      "Online randevu / rezervasyon talebi sistemi",
      "Türkçe ve İngilizce, gelişmiş arama motoru kurulumu",
      "12 ay teknik destek ve küçük güncellemeler",
    ],
  },
];

const works = [
  {
    name: "HAVEK",
    text: "Havacılık eğitim ekipmanları ve simülatör çözümleri sunan firma.",
    result: "Yeni sitenin ardından etkileşim %70 arttı.",
    note: "HAVEK'in bildirdiği sonuç",
    image: "/business-works/havek.jpg",
    href: "https://havek.tr",
  },
  {
    name: "Untitled Robotics",
    text: "Şu anda yatırım arayan robotik ve yapay zeka girişimi.",
    result: "Görüştükleri potansiyel yatırımcı sayısı %27 arttı.",
    note: "Girişim ekibinin bildirdiği sonuç",
    image: "/business-works/untitled-robotics.jpg",
    href: "https://untitledrobotics.com",
  },
];

const steps = [
  { n: "01", title: "Bilgileri gönderin", text: "İşletme adı, hizmetler, çalışma saatleri ve 3-5 fotoğraf yeterli." },
  { n: "02", title: "Taslağı görün", text: "İlk taslağı 24 saat içinde telefonunuzda görürsünüz; düzeltme isteklerinizi iletirsiniz." },
  { n: "03", title: "Yayına alalım", text: "Onayınızdan sonra siteniz yayında. Teslimden sonra 1 revizyon turu pakete dahildir." },
];

const faqs = [
  {
    q: "Alan adı (isletmeadi.com gibi) gerekli mi?",
    a: "Varsa bağlarım. Yoksa seçmenize ve kaydetmenize yardımcı olurum. Alan adının yıllık bedeli işletmenize aittir ve fiyatlara dahil değildir.",
  },
  {
    q: "Sayfa Google'da ilk sırada çıkar mı?",
    a: "Haritalar kaydınızla uyumlu başlık, açıklama ve işaretlemeyi hazırlarım. Sıralamayı Google belirler, bu yüzden sıralama garantisi vermem.",
  },
  {
    q: "Ödeme nasıl yapılıyor?",
    a: "Kapsam, teslim tarihi ve fiyat önce yazılı olarak netleştirilir; ödeme planı teklifte yer alır ve fatura düzenlenir. Onayınız olmadan işe başlanmaz.",
  },
  {
    q: "Sonradan değişiklik yapabilir miyim?",
    a: "Evet. Saat, fiyat, telefon, fotoğraf gibi küçük güncellemeler için yazmanız yeterli. Kapsamlı değişiklikler ayrıca fiyatlandırılır.",
  },
  {
    q: "Kiminle çalışacağım?",
    a: "İstanbul'da serbest çalışan yazılımcı Ali Renan Uyanık ile. Çalışmalarımı ana sayfada inceleyebilirsiniz.",
  },
];

const fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function Business() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "İşletmeniz için web sitesi — Ali Renan Uyanık";
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute(
      "content",
      "Yerel işletmeler için tek sayfalık, mobil uyumlu web sitesi. 1.499 TL'den başlayan paketler, 2 günde teslim."
    );
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <main data-testid="business-root" className="relative bg-black text-white min-h-screen font-sans">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-lg tracking-tight">
            Ali Renan <span className="text-zinc-500">· Web geliştirici</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#paketler" className="hover:text-white transition-colors">Paketler</a>
            <a href="#referanslar" className="hover:text-white transition-colors">Referanslar</a>
            <a href="#surec" className="hover:text-white transition-colors">Süreç</a>
            <a href="#sss" className="hover:text-white transition-colors">Sık sorulanlar</a>
          </nav>
          <a
            href={mailHref}
            className="inline-flex items-center gap-2 rounded-full bg-white text-black text-sm font-medium px-4 py-2 hover:bg-zinc-200 transition-colors"
          >
            Teklif al
          </a>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 radial-glow pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 md:pt-32 md:pb-32">
          <motion.p {...fade} className="text-sm text-blue-400 tracking-wide">
            Yerel işletmeler için web sitesi
          </motion.p>
          <motion.h1
            {...fade}
            className="font-heading mt-5 text-5xl sm:text-6xl md:text-7xl font-light tracking-[-0.035em] leading-[1.02]"
          >
            Google'da varsınız.
            <br />
            <span className="text-zinc-500">Peki web siteniz?</span>
          </motion.h1>
          <motion.p {...fade} className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-300 leading-relaxed">
            Müşterileriniz sizi aradığında hizmetlerinizi, çalışma saatlerinizi ve konumunuzu tek sayfada görsün;
            tek dokunuşla arasın ya da yol tarifi alsın. Telefonda hızlı açılan işletme siteniz 2 günde hazır.
          </motion.p>
          <motion.div {...fade} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={mailHref}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 transition-colors text-white font-medium px-7 py-4"
            >
              Ücretsiz teklif al <ArrowUpRight className="w-5 h-5" />
            </a>
            <a
              href="#paketler"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/50 transition-colors px-7 py-4"
            >
              Paketleri gör
            </a>
          </motion.div>
          <motion.p {...fade} className="mt-8 text-sm text-zinc-500">
            İstanbul · Teklif ücretsizdir ve bağlayıcı değildir · Fiyatlar paket başına sabittir
          </motion.p>
        </div>
      </section>

      <section className="relative border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 grid gap-6 md:grid-cols-3">
          {reasons.map((r, i) => (
            <motion.div
              key={r.title}
              {...fade}
              transition={{ ...fade.transition, delay: i * 0.08 }}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-8"
            >
              <r.icon className="w-7 h-7 text-blue-400" strokeWidth={1.5} />
              <h3 className="font-heading mt-6 text-2xl font-light">{r.title}</h3>
              <p className="mt-3 text-zinc-400 leading-relaxed">{r.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="paketler" className="relative border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <motion.h2 {...fade} className="font-heading text-4xl md:text-5xl font-light tracking-tight">
            Net paketler, <span className="text-zinc-500">net fiyatlar.</span>
          </motion.h2>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {plans.map((p, i) => (
              <motion.div
                key={p.name}
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.08 }}
                className={`relative rounded-2xl p-8 flex flex-col ${
                  p.highlight ? "border border-blue-500/60 bg-blue-500/[0.07]" : "border border-white/10 bg-white/[0.03]"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-8 rounded-full bg-blue-600 text-xs font-medium px-3 py-1">
                    Önerilen
                  </span>
                )}
                <h3 className="font-heading text-2xl font-light">{p.name}</h3>
                <p className="mt-4 font-heading text-5xl font-light tracking-tight">{p.price}</p>
                <p className="mt-2 inline-flex items-center gap-2 text-sm text-zinc-400">
                  <Clock className="w-4 h-4" /> {p.time}
                </p>
                <ul className="mt-8 space-y-3 text-zinc-300 flex-1">
                  {p.items.map((it) => (
                    <li key={it} className="flex gap-3 leading-relaxed">
                      <Check className="w-5 h-5 mt-0.5 shrink-0 text-blue-400" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={mailHref}
                  className={`mt-10 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-medium transition-colors ${
                    p.highlight ? "bg-blue-600 hover:bg-blue-500" : "border border-white/20 hover:border-white/50"
                  }`}
                >
                  {p.name} için teklif al
                </a>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-sm text-zinc-500 max-w-3xl">
            Fiyatlara alan adı yıllık bedeli dahil değildir. Kapsamı değişen ya da ek sayfa isteyen projeler için
            fiyat, işe başlamadan önce yazılı olarak netleştirilir.
          </p>
        </div>
      </section>

      <section id="referanslar" className="relative border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <motion.h2 {...fade} className="font-heading text-4xl md:text-5xl font-light tracking-tight">
            Birlikte çalıştığımız <span className="text-zinc-500">firmalar.</span>
          </motion.h2>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {works.map((w, i) => (
              <motion.a
                key={w.name}
                href={w.href}
                target="_blank"
                rel="noopener noreferrer"
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.08 }}
                className="group overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] hover:border-white/30 transition-colors"
              >
                <img
                  src={w.image}
                  alt={`${w.name} web sitesi`}
                  loading="lazy"
                  className="w-full aspect-[16/9] object-cover object-top border-b border-white/10"
                />
                <div className="p-8">
                  <p className="font-heading text-2xl font-light flex items-center justify-between gap-4">
                    {w.name}
                    <ArrowUpRight className="w-6 h-6 text-zinc-500 group-hover:text-white transition-colors" />
                  </p>
                  <p className="mt-3 text-zinc-400 leading-relaxed">{w.text}</p>
                  <p className="mt-5 font-heading text-xl font-light text-white">{w.result}</p>
                  <p className="mt-1 text-xs text-zinc-500">{w.note}</p>
                  <p className="mt-6 text-sm text-blue-400">{w.href.replace("https://", "")}</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      <section id="surec" className="relative border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <motion.h2 {...fade} className="font-heading text-4xl md:text-5xl font-light tracking-tight">
            Üç adımda <span className="text-zinc-500">yayında.</span>
          </motion.h2>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {steps.map((s, i) => (
              <motion.div key={s.n} {...fade} transition={{ ...fade.transition, delay: i * 0.08 }}>
                <p className="font-mono text-sm text-blue-400">{s.n}</p>
                <h3 className="font-heading mt-3 text-2xl font-light">{s.title}</h3>
                <p className="mt-3 text-zinc-400 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-14 flex flex-wrap gap-6 text-zinc-400 text-sm">
            <span className="inline-flex items-center gap-2"><Smartphone className="w-4 h-4" /> Telefon, tablet ve bilgisayarda düzgün görünür</span>
            <span className="inline-flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Sorularınıza aynı gün yanıt</span>
          </div>
        </div>
      </section>

      <section id="sss" className="relative border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <motion.h2 {...fade} className="font-heading text-4xl md:text-5xl font-light tracking-tight">
            Sık sorulanlar
          </motion.h2>
          <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
            {faqs.map((f) => (
              <details key={f.q} className="group py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg md:text-xl font-heading font-light">
                  {f.q}
                  <span className="text-zinc-500 transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
                </summary>
                <p className="mt-4 text-zinc-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="iletisim" className="relative border-t border-white/10 overflow-hidden">
        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[120vw] h-[60vw] max-w-[1400px] rounded-full bg-blue-500/[0.07] blur-3xl pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 py-24 md:py-32 text-center">
          <motion.h2 {...fade} className="font-heading text-4xl md:text-6xl font-light tracking-tight leading-[1.05]">
            İşletmeniz için ücretsiz teklif alın.
          </motion.h2>
          <motion.p {...fade} className="mt-6 text-lg text-zinc-400 max-w-2xl mx-auto">
            İşletme adınızı ve sektörünüzü yazmanız yeterli. Size aynı gün dönüş yapıp kapsamı ve fiyatı netleştirelim.
            Bu sayfaya WhatsApp'tan size yazdığım hat üzerinden geldiyseniz aynı hattan da yanıt verebilirsiniz.
          </motion.p>
          <motion.div {...fade} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href={mailHref}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 transition-colors font-medium px-8 py-4"
            >
              E-posta ile teklif iste <ArrowUpRight className="w-5 h-5" />
            </a>
            <a
              href={profile.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:border-white/50 transition-colors px-8 py-4"
            >
              Instagram'dan yazın
            </a>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-10 text-center text-sm text-zinc-500">
        <p>
          © {new Date().getFullYear()} Ali Renan Uyanık ·{" "}
          <Link to="/" className="underline decoration-zinc-700 underline-offset-4 hover:text-white">
            Ana sayfa
          </Link>
        </p>
      </footer>
    </main>
  );
}
